import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  effect,
  inject,
  input,
  output,
  ElementRef,
  viewChild,
  untracked,
  HostListener,
  afterNextRender,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import type { FocusTrap } from '@angular/cdk/a11y';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { SecurepayNotification } from '../notification.model';
import { NotificationItemComponent } from '../notification-item/notification-item.component';
import { NotificationStore } from '../notification.store';
import { ChargebackStore } from '../../../../../securepay-merchant-revamp/src/app/shared/data/store/chargeback.store';
import { EmptyState } from '../../components/empty-state/empty-state';

@Component({
  selector: 'app-notification-drawer',
  standalone: true,
  imports: [NotificationItemComponent, EmptyState],
  templateUrl: './notification-drawer.component.html',
  styleUrl: './notification-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(0)' }),
        ),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
    trigger('fadeBackdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('180ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('160ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class NotificationDrawerComponent {
  private readonly document = inject(DOCUMENT);
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  protected readonly store = inject(NotificationStore);

  private trap: FocusTrap | null = null;
  private restoreFocusEl: HTMLElement | null = null;

  protected readonly drawerPanel =
    viewChild<ElementRef<HTMLElement>>('drawerPanel');

  /** When true, the drawer is visible. */
  readonly open = input(false);
  readonly closed = output<void>();

  constructor(
    private readonly chargeBackStore: ChargebackStore,
    // setSelectedChargeback
  ) {
    this.destroyRef.onDestroy(() => this.releaseFocusTrap());

    effect(() => {
      const isOpen = this.open();
      if (!isOpen) {
        untracked(() => this.releaseFocusTrap());
        return;
      }

      untracked(() => {
        const pageSize = this.store.pagination().pageSize;
        this.store.loadNotifications(0, pageSize, { replace: true });
      });

      afterNextRender(
        () => {
          untracked(() => void this.activateFocusTrap());
        },
        { injector: this.injector },
      );
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  protected close(): void {
    this.closed.emit();
  }

  protected onBackdropKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.close();
    }
  }

  protected onAccept(n: SecurepayNotification): void {
    this.chargeBackStore.setSelectedChargeback(n.metadata);
    this.chargeBackStore.onAcceptChargeback();
  }

  protected onDecline(n: SecurepayNotification): void {
    this.chargeBackStore.setSelectedChargeback(n.metadata);
    this.chargeBackStore.onDeclineChargeback();
  }

  private async activateFocusTrap(): Promise<void> {
    this.restoreFocusEl = this.document.activeElement as HTMLElement | null;
    const el = this.drawerPanel()?.nativeElement;
    if (!el) {
      return;
    }
    this.trap?.destroy();
    this.trap = this.focusTrapFactory.create(el);
    await this.trap.focusInitialElementWhenReady();
    const closeBtn = el.querySelector<HTMLElement>(
      '[data-notification-drawer-close]',
    );
    closeBtn?.focus();
  }

  private releaseFocusTrap(): void {
    this.trap?.destroy();
    this.trap = null;
    const el = this.restoreFocusEl;
    this.restoreFocusEl = null;
    el?.focus?.();
  }
}
